import {gql, useQuery} from '@apollo/client';
import {useEffect, useMemo} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {every} from 'lodash-es';
import * as Sentry from '@sentry/react';
import {NOTE_DETAILS_FRAGMENT, REVIEW_DETAILS_FRAGMENT} from '../../graphql/fragments';
import {useReviewOVRecommends} from '../../hooks/useOVRecommends';
import {ReviewDetails, ReviewDetailsVariables} from '../../schema-types/ReviewDetails';

import {
  handoverStepOrder,
  HandoverStepType,
  stepValidationType,
} from './HandoverStepTypes';
import {hasPermission} from '../../contexts/FeatureBoundary';
import {
  GlobalPermissionEnum,
  ReviewStatusOneviewEnum,
} from '../../schema-types/globalTypes';
import useDjango from '../../hooks/useDjango';
import {useAccountContext} from '../../contexts/AccountContext';
import validateUUID from '../../utils/validateUUID';

export const REVIEW_VALIDATION = gql`
  query ReviewDetails($valueId: UUID!, $activeOnly: Boolean) {
    reviewDetails(keyId: "review_id", valueId: $valueId, activeOnly: $activeOnly) {
      ...ReviewDetailsFragment
      ...HandoverNotesReviewDetailsFragment
      requireRiskProfile
      submitter {
        id
        name
      }
      account {
        id
        isFailedSubmission
        isDeleted
        isTrust
      }
      contact {
        id
        isFailedSubmission
        isDeleted
      }
      recommendations {
        id
        curoId
        isFailedSubmission
        isDeleted
      }
      goals {
        id
        isFailedSubmission
        isDeleted
        isInCuro
      }
      tasks {
        id
        isFailedSubmission
        curoId
        activityStatus
        isDeleted
      }
    }
  }
  ${NOTE_DETAILS_FRAGMENT}
  ${REVIEW_DETAILS_FRAGMENT}
`;

const canAccessReportPackStatuses: ReviewStatusOneviewEnum[] = [
  ReviewStatusOneviewEnum.CLOSED,
  ReviewStatusOneviewEnum.COMPLETED,
];

export const useReviewSteps = (
  reviewId?: string
): {
  stepValidation: Partial<stepValidationType>;
  allValid: boolean;
  loadingReview: boolean;
  steps: HandoverStepType[];
  currentStep: HandoverStepType;
  currentStepIndex: number;
  previousStep: HandoverStepType | null;
  nextStep: HandoverStepType | null;
  error: Error | null;
  navigateToHandoverPage: (step: HandoverStepType) => void;
  getHandoverPageURL: (step: HandoverStepType) => string;
  reviewValidation: ReviewDetails['reviewDetails'] | null;
  refreshReview: () => void;
  noChanges: boolean;
  fullValidationLoading: boolean;
  softSubmitValid: boolean;
} => {
  const navigate = useNavigate();
  const {djangoUser} = useDjango();
  const {account} = useAccountContext();
  const {accountId, reviewId: paramsReviewId} = useParams();
  const {
    data,
    loading: loadingReview,
    error,
    refetch,
  } = useQuery<ReviewDetails, ReviewDetailsVariables>(REVIEW_VALIDATION, {
    variables: {
      valueId: reviewId || validateUUID(paramsReviewId) || '',
    },
    skip: !reviewId && !paramsReviewId,
  });

  const canAccessReportPack = hasPermission(djangoUser, [
    GlobalPermissionEnum.can_access_report_pack,
  ]);
  const canAccessRisk = hasPermission(djangoUser, [
    GlobalPermissionEnum.can_access_risk_profile,
  ]);
  const canAccessRiskNotes = hasPermission(djangoUser, [
    GlobalPermissionEnum.can_access_risk_notes,
  ]);
  const canAccessDentistBuilder = hasPermission(djangoUser, [
    GlobalPermissionEnum.can_access_dentist_recommendation_builder,
  ]);

  // add sentry context of reviewId reviewCuroId
  useEffect(() => {
    if (data?.reviewDetails) {
      try {
        Sentry.setTag('reviewId', reviewId || '');
        Sentry.setTag('reviewCuroId', data.reviewDetails.curoId || '');
      } catch (err) {
        // do nothing
      }
    }
  }, [data?.reviewDetails, reviewId]);

  const steps = useMemo(() => {
    if (!data?.reviewDetails) {
      return [];
    }
    return handoverStepOrder.filter((step) => {
      if (step === 'report') {
        return canAccessReportPack;
      }
      if (step === 'risknotes') {
        return canAccessRiskNotes && data?.reviewDetails?.requireRiskProfile;
      }
      if (step === 'risk') {
        if (canAccessRiskNotes) {
          return false;
        }
        // only show old risk if they cant view the new one
        return data?.reviewDetails?.requireRiskProfile;
      }
      if (step === 'requirements') {
        return !canAccessDentistBuilder;
      }
      if (step === 'requirements-builder') {
        return canAccessDentistBuilder;
      }
      return true;
    });
  }, [
    data?.reviewDetails,
    canAccessReportPack,
    account?.isTrust,
    canAccessRisk,
    canAccessRiskNotes,
    canAccessDentistBuilder,
  ]);

  const location = useLocation();
  const currentStep = useMemo(() => {
    return (
      steps.reduce<HandoverStepType | null>((currentStepOrNull, step) => {
        if (location.pathname.includes(step)) {
          return step;
        }
        return currentStepOrNull;
      }, null) || 'meeting'
    );
  }, [steps, location]);

  const currentStepIndex = useMemo(
    () => steps.indexOf(currentStep),
    [steps, currentStep]
  );
  const nextStepPlain = useMemo(() => {
    return steps.length - currentStepIndex > 1 ? steps[currentStepIndex + 1] : null;
  }, [steps, currentStepIndex]);
  const previousStep =
    steps.length - currentStepIndex === 0 ? null : steps[currentStepIndex - 1];

  const nextStep = useMemo(() => {
    if (!nextStepPlain) {
      return null;
    }
    if (nextStepPlain === 'report') {
      if (
        data?.reviewDetails?.statusOneview &&
        canAccessReportPackStatuses.includes(data?.reviewDetails?.statusOneview)
      ) {
        return nextStepPlain;
      }
      return null;
    }
    return nextStepPlain;
  }, [nextStepPlain, data?.reviewDetails?.statusOneview]);

  const {data: recsData} = useReviewOVRecommends();

  const stepValidation: Partial<stepValidationType> = {
    meeting: !data?.reviewDetails?.validation?.header?.length,
    resilience: true,
    goals: !data?.reviewDetails?.validation?.goals?.length,

    notes: !data?.reviewDetails?.validation?.notes?.length,
    requirements: !data?.reviewDetails?.validation?.recs?.length,
    'requirements-builder': !data?.reviewDetails?.validation?.recs?.length,
    tasks: !data?.reviewDetails?.validation?.tasks?.length,
    report: true,
  };

  if (canAccessRiskNotes) {
    stepValidation.risknotes = !data?.reviewDetails?.validation?.risk?.length;
  } else if (canAccessRisk) {
    stepValidation.risk = !data?.reviewDetails?.validation?.risk?.length;
  }

  const noChanges = !!data?.reviewDetails?.validation?.noChanges?.length;
  // Tiger has advised me to change to below to unblock me while he figures out bug related to fees.
  const allValid =
    Object.values(stepValidation).every((step) => step === true) && !noChanges;
  const softSubmitValid =
    every(stepValidation, (val, key) => {
      if (key === 'risk' || key === 'risknotes') {
        return true; // Skip risk validation for soft submit
      }
      return val === true;
    }) && !noChanges;

  const navigateToHandoverPage = (step: HandoverStepType) => {
    navigate({pathname: getHandoverPageURL(step)});
  };

  const getHandoverPageURL = (step: HandoverStepType) => {
    return `/account/${accountId}/review-journey/${reviewId}/${step}`;
  };

  const validationLoading = !data || !recsData;

  return {
    allValid,
    currentStep,
    currentStepIndex,
    noChanges,
    error: error || null,
    getHandoverPageURL,
    loadingReview,
    navigateToHandoverPage,
    nextStep,
    previousStep,
    softSubmitValid,
    reviewValidation: data?.reviewDetails || null,
    stepValidation,
    steps,
    fullValidationLoading: validationLoading,
    refreshReview: () =>
      refetch({
        valueId: reviewId || validateUUID(paramsReviewId) || '',
      }),
  };
};
